import { IMG } from './site';

export const BLOG_POSTS = [
  {
    slug: 'medical-vs-therapeutic-massage',
    title: 'Medical Massage vs. Therapeutic Massage: What\u2019s the Difference?',
    category: 'Education',
    date: 'April 2, 2025',
    readTime: '4 min read',
    cover: IMG.therapeutic,
    excerpt:
      'Both happen on the same table — so what actually separates them? Referrals, treatment plans, goals, and billing, explained plainly.',
    sections: [
      {
        h: 'Two terms, two purposes',
        p: [
          'Medical massage is outcome-driven treatment for a specific condition — usually one your doctor, surgeon, or VA provider has identified. It follows a structured plan: assessment, focused techniques aimed at that condition, and documentation of how your body responds from visit to visit. Think post-surgical recovery, injury rehabilitation, sciatica, or service-connected chronic pain.',
          'Therapeutic massage is personalized bodywork built around how you present that day. There\u2019s no referral and no diagnosis driving the session — the goal is overall relief: tension, stress, sleep, general wellbeing. Your therapist blends techniques and adjusts pressure and focus in real time.',
        ],
      },
      {
        h: 'The key differences',
        p: [
          'Referral and paperwork: medical massage often begins with a provider\u2019s referral or authorization (through programs like VA Community Care), and sessions are documented so your care team can follow your progress. Therapeutic massage is self-directed — you book it because you want it.',
          'Plan vs. session: medical massage follows a treatment plan with measurable goals across multiple visits. Therapeutic massage is shaped session by session, around how you arrive that day.',
          'Billing: medical massage may be billed through insurance or a VA authorization when one is in place. Therapeutic massage is a straightforward self-pay service, though HSA/FSA cards often work.',
        ],
      },
      {
        h: 'How they work together',
        p: [
          'They\u2019re not rivals — they\u2019re teammates. Many clients use medical massage to address a specific condition while therapeutic sessions maintain the gains, calm the nervous system, and keep the rest of the body happy in between.',
        ],
      },
      {
        h: 'Which should you book?',
        p: [
          'Recovering from surgery or injury, managing a service-connected or diagnosed condition, or holding a referral? Medical massage. General tension, stress, sleep, or a first visit with us? Therapeutic — your therapist will tell you honestly if a medical track makes sense next.',
          'Still unsure? Book a therapeutic session and start the conversation. The intake exists precisely so we can point you in the right direction.',
        ],
      },
    ],
  },
  {
    slug: 'prenatal-massage-trimester-by-trimester',
    title: 'Prenatal Massage, Trimester by Trimester',
    category: 'Pregnancy',
    date: 'March 12, 2025',
    readTime: '4 min read',
    cover: IMG.prenatal,
    excerpt:
      'What\u2019s safe, what changes each trimester, and how side-lying positioning makes every stage comfortable.',
    sections: [
      {
        h: 'First trimester: go gently, or simply rest',
        p: [
          'The first twelve weeks are a time of enormous internal work, and many therapists (and most obstetric guidelines) suggest waiting until the second trimester for full-body sessions. If you\u2019re exhausted or nauseated in those early months, a light, restful session focused on the neck, shoulders, and feet can be wonderful — always after a quick check-in with your provider.',
        ],
      },
      {
        h: 'Second trimester: the sweet spot',
        p: [
          'Energy usually returns just as your body starts asking for help. Low-back ache, hip tightness, and swelling often arrive together, and this is when prenatal massage does its best work. Sessions are side-lying with cushioned bolsters under your bump, knees, and ankles — no face-down pads, ever.',
        ],
      },
      {
        h: 'Third trimester: support, support, support',
        p: [
          'By the final months, massage is less about depth and more about relief: gentle work through the low back and hips, fluid-easing strokes for swollen legs, and quiet time for a nervous system that\u2019s been running on high alert. Many clients continue weekly or biweekly sessions right up to their due date.',
        ],
      },
      {
        h: 'The throughline: communication',
        p: [
          'Every trimester brings changes, and every body carries pregnancy differently. We check pressure and positioning throughout the session — you\u2019re never asked to tolerate discomfort for the sake of the treatment. Have a high-risk pregnancy or specific concerns? Message us before booking and we\u2019ll talk it through.',
        ],
      },
    ],
  },
  {
    slug: 'lymphatic-drainage-101',
    title: 'Lymphatic Drainage 101: What It Is and Who It Helps',
    category: 'Education',
    date: 'February 18, 2025',
    readTime: '4 min read',
    cover: IMG.lymphatic,
    excerpt:
      'The lightest touch in massage therapy does some of the heaviest lifting. Here\u2019s how the lymphatic system — and the technique — actually works.',
    sections: [
      {
        h: 'A system without a pump',
        p: [
          'Your lymphatic system moves fluid, waste, and immune cells through the body — but unlike blood, it has no heart to pump it. Lymph moves through vessel valves aided by your breath, muscle movement, and gentle external pressure. When that flow stalls, fluid collects: puffiness, heaviness, swelling.',
        ],
      },
      {
        h: 'Why the touch is so light',
        p: [
          'Lymph vessels sit just beneath the skin — far shallower than muscle. Lymphatic drainage uses feather-light, rhythmic, precisely-sequenced strokes to nudge flow along without pressing the vessels closed. First-time clients are often surprised that something so gentle can be so effective; the lightness is the technique.',
        ],
      },
      {
        h: 'Who benefits',
        p: [
          'Post-surgical swelling (with your surgeon\u2019s clearance and timing), persistent puffiness, sluggish recovery after illness, and the general heaviness that comes with stress or long sedentary stretches. Athletes use it for recovery; pre- and post-natal clients use it for fluid shifts in pregnancy.',
        ],
      },
      {
        h: 'What a session feels like',
        p: [
          'Slow, quiet, and rhythmic — closer to a moving meditation than a workout. Most clients notice warmth and lightness as sessions progress, and sleep unusually well that night. It pairs beautifully with therapeutic massage when your goal is both relief and recovery.',
        ],
      },
    ],
  },
  {
    slug: 'deep-tissue-or-therapeutic',
    title: 'Deep Tissue or Therapeutic? How to Choose',
    category: 'Education',
    date: 'January 22, 2025',
    readTime: '3 min read',
    cover: IMG.deepTissue,
    excerpt:
      'One isn\u2019t the "harder" version of the other. Here\u2019s how to pick the session your body is actually asking for.',
    sections: [
      {
        h: 'Therapeutic massage: the whole-body reset',
        p: [
          'Therapeutic massage is personalized, medically-informed bodywork that treats the session as a conversation — intake first, then a blend of techniques matched to how you present that day. It\u2019s the right choice for general tension, stress, sleep, and anyone who wants skilled care without a specific target.',
        ],
      },
      {
        h: 'Deep tissue: the focused intervention',
        p: [
          'Deep tissue is slower and more specific, working layer by layer into chronic tension and adhesions — the shoulder that hasn\u2019t let go in years, the low back that locks up every winter. Depth is a tool, not the goal: productive pressure feels like release, and we check in constantly.',
        ],
      },
      {
        h: 'A simple rule of thumb',
        p: [
          'New ache, whole-body stress, first visit? Start therapeutic. A specific, stubborn pattern you already know well? Deep tissue. Unsure? Book therapeutic — your therapist will tell you honestly whether a deeper, more focused session makes sense next time.',
        ],
      },
    ],
  },
  {
    slug: 'massage-and-the-va-community-care',
    title: 'Massage and the VA: How Community Care Referrals Work',
    category: 'Veterans',
    date: 'December 10, 2024',
    readTime: '4 min read',
    cover: IMG.veterans,
    excerpt:
      'If you\u2019re enrolled in VA health care, massage therapy may be available to you in the community. Here\u2019s the path, step by step.',
    sections: [
      {
        h: 'What Community Care is',
        p: [
          'Through the VA Community Care Network, enrolled veterans can receive approved care from providers outside VA facilities when the services aren\u2019t readily available within the VA, or when it makes practical sense for the veteran. Massage therapy is one of the services that can be authorized this way.',
        ],
      },
      {
        h: 'Step one: the referral',
        p: [
          'It starts with your VA provider. Tell them about the pain, tension, or stiffness you\u2019re living with and ask whether a Community Care referral for massage therapy makes sense for you. If they agree, the VA issues an authorization covering a set number of visits with a community provider.',
        ],
      },
      {
        h: 'Step two: scheduling with us',
        p: [
          'Bring your authorization details to us — a quick call to (832) 350-8516 — and we\u2019ll confirm the paperwork before your first visit, so nothing about billing is a surprise. Authorized visits are billed to the VA; you\u2019re not paying out of pocket for covered care.',
        ],
      },
      {
        h: 'What the sessions are like',
        p: [
          'Measured, communicative, and built around your service-connected conditions and medications. The first session is deliberately conservative so we can learn how your body responds. You set the pace — and you\u2019re always in control of pressure and focus.',
        ],
      },
    ],
  },
  {
    slug: 'the-fourth-trimester',
    title: 'The Fourth Trimester: Massage in Postpartum Recovery',
    category: 'Postpartum',
    date: 'November 5, 2024',
    readTime: '4 min read',
    cover: IMG.doula,
    excerpt:
      'The baby arrives and the world asks about the baby. Postpartum massage is an hour that asks about you.',
    sections: [
      {
        h: 'A body still doing the work',
        p: [
          'Recovery doesn\u2019t end at birth — it begins there. Hormones shift, joints settle back, and the muscles of feeding, carrying, and rocking a newborn take on a repetitive load that shows up as neck and shoulder tension, wrist strain, and a low back that never quite lets go.',
        ],
      },
      {
        h: 'What postpartum sessions target',
        p: [
          'We keep work gentle and specific: releasing the neck and shoulder load, easing the forearms and wrists of carrying, supporting the low back and core, and — just as important — giving your nervous system an hour of uninterrupted down-regulation. For C-section recovery, we work around and gradually toward the incision area with your provider\u2019s guidance.',
        ],
      },
      {
        h: 'Timing and logistics',
        p: [
          'Most clients feel ready within the first few weeks, but there\u2019s no deadline — fourth trimester care is valuable whether you\u2019re six weeks or six months postpartum. Come as you are; sessions are shaped around feeding schedules, tenderness, and the simple reality of a changing routine.',
        ],
      },
      {
        h: 'You\u2019re allowed to rest',
        p: [
          'If pregnancy and birth were a marathon, postpartum is the long, quiet recovery afterward — and it deserves the same intentionality. An hour on the table won\u2019t solve everything, but it reliably reminds your body what settling feels like. Book online whenever you\u2019re ready.',
        ],
      },
    ],
  },
];

export function getPost(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
